<script setup lang="ts">
/**
 * H5b — FAIR Services promo cards
 * Figma RedisignHomePage `15:9694` / compact `15:9605`
 * Order: Style Space → Size charts → Taste
 *
 * Hover video: still = frame 0 of the loop. Pointer enter → play; pointer
 * leave does NOT stop it — the loop runs to its end (last frame ≡ frame 0),
 * then the still is shown again. If the pointer is still on the card at the
 * end, the loop restarts.
 *
 * Compact (`<1024`): cards are a horizontal scroll row (Figma `15:9609`).
 * No hover — a card's video plays once it is scrolled into view
 * (IntersectionObserver on the media box, ≥ 60 % visible) and keeps looping
 * while it stays in view; leaving the viewport lets the loop finish.
 */
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useFairDragScroll } from '@/composables/useFairDragScroll'
import { FairImage } from '@/ui/primitives/FairImage'
import { useStyleQuizStore } from '@/features/style-quiz/store'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import {
  FAIR_HOME_V2_SERVICES,
  type FairHomeV2ServiceCard,
  type FairHomeV2ServiceId,
} from './fair-home-v2-services.data'
import './fair-home-v2-services.css'

const props = withDefaults(
  defineProps<{
    compact?: boolean
  }>(),
  {
    compact: false,
  },
)

const { t } = useI18n()
const router = useRouter()
const styleQuiz = useStyleQuizStore()

const cards = computed(() => FAIR_HOME_V2_SERVICES)

const rootClass = computed(() => ({
  'fair-home-v2-services': true,
  'fair-home-v2-services--compact': props.compact,
  'fair-home-v2-services--desktop': !props.compact,
}))

const gridRef = ref<HTMLElement | null>(null)
/* Drag-to-scroll only for the compact row — on desktop the grid does not
   scroll and pointer capture would break the hover video enter/leave. */
const dragScrollTarget = computed(() =>
  props.compact ? gridRef.value : null,
)
useFairDragScroll(dragScrollTarget)

/* ------------------------------------------------------------------ */
/* Hover video                                                          */
/* ------------------------------------------------------------------ */

const videoRefs = new Map<FairHomeV2ServiceId, HTMLVideoElement>()
const playing = reactive<Record<FairHomeV2ServiceId, boolean>>({
  'style-space': false,
  'size-charts': false,
  'taste-feed': false,
})
const hovered = reactive<Record<FairHomeV2ServiceId, boolean>>({
  'style-space': false,
  'size-charts': false,
  'taste-feed': false,
})

/** Compact: card media currently ≥ 60 % inside the viewport. */
const inView = reactive<Record<FairHomeV2ServiceId, boolean>>({
  'style-space': false,
  'size-charts': false,
  'taste-feed': false,
})

/** Hover video only where a pointer can hover and motion is welcome. */
const canHoverVideo = ref(false)
/** Scroll-into-view video (compact) — only when motion is welcome. */
const canScrollVideo = ref(false)
let hoverQuery: MediaQueryList | null = null
let motionQuery: MediaQueryList | null = null

function updateVideoCapabilities() {
  const reduced = Boolean(motionQuery?.matches)
  canHoverVideo.value = Boolean(hoverQuery?.matches) && !reduced
  canScrollVideo.value = !reduced
}

/* Scroll-into-view (compact) */
const mediaRefs = new Map<FairHomeV2ServiceId, HTMLElement>()
const mediaIds = new WeakMap<Element, FairHomeV2ServiceId>()
let viewObserver: IntersectionObserver | null = null

const SCROLL_VIDEO_VISIBLE_RATIO = 0.6

function handleIntersections(entries: IntersectionObserverEntry[]) {
  for (const entry of entries) {
    const id = mediaIds.get(entry.target)
    if (!id) continue
    const visible =
      entry.isIntersecting
      && entry.intersectionRatio >= SCROLL_VIDEO_VISIBLE_RATIO
    inView[id] = visible
    if (visible && canScrollVideo.value) {
      startVideo(id)
    }
  }
}

function disconnectViewObserver() {
  viewObserver?.disconnect()
  viewObserver = null
}

function connectViewObserver() {
  disconnectViewObserver()
  if (
    !props.compact
    || typeof window === 'undefined'
    || !('IntersectionObserver' in window)
  ) {
    return
  }
  viewObserver = new IntersectionObserver(handleIntersections, {
    threshold: [0, SCROLL_VIDEO_VISIBLE_RATIO, 1],
  })
  mediaRefs.forEach((el) => viewObserver?.observe(el))
}

function setMediaRef(id: FairHomeV2ServiceId, el: unknown) {
  const prev = mediaRefs.get(id)
  if (prev && prev !== el) {
    viewObserver?.unobserve(prev)
    mediaRefs.delete(id)
  }
  if (el instanceof HTMLElement) {
    mediaRefs.set(id, el)
    mediaIds.set(el, id)
    viewObserver?.observe(el)
  }
}

onMounted(() => {
  if (typeof window === 'undefined' || !window.matchMedia) return
  hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)')
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  hoverQuery.addEventListener('change', updateVideoCapabilities)
  motionQuery.addEventListener('change', updateVideoCapabilities)
  updateVideoCapabilities()
  connectViewObserver()
})

watch(
  () => props.compact,
  () => {
    playing['style-space'] = false
    playing['size-charts'] = false
    playing['taste-feed'] = false
    videoRefs.forEach((video) => video.pause())
    connectViewObserver()
  },
)

onBeforeUnmount(() => {
  hoverQuery?.removeEventListener('change', updateVideoCapabilities)
  motionQuery?.removeEventListener('change', updateVideoCapabilities)
  disconnectViewObserver()
  mediaRefs.clear()
  videoRefs.forEach((video) => video.pause())
  videoRefs.clear()
})

function setVideoRef(id: FairHomeV2ServiceId, el: unknown) {
  if (el instanceof HTMLVideoElement) {
    videoRefs.set(id, el)
  } else {
    videoRefs.delete(id)
  }
}

function startVideo(id: FairHomeV2ServiceId) {
  const video = videoRefs.get(id)
  if (!video || playing[id]) return
  // After `ended` the element sits on the last frame (≡ frame 0); play()
  // restarts from 0 without a visible seek.
  const attempt = video.play()
  if (attempt && typeof attempt.catch === 'function') {
    attempt.catch(() => {
      playing[id] = false
    })
  }
}

function handlePointerEnter(id: FairHomeV2ServiceId) {
  hovered[id] = true
  if (!canHoverVideo.value) return
  startVideo(id)
}

function handlePointerLeave(id: FairHomeV2ServiceId) {
  // Intentionally no pause — the loop finishes on its own.
  hovered[id] = false
}

function handlePlaying(id: FairHomeV2ServiceId) {
  playing[id] = true
}

function handleEnded(id: FairHomeV2ServiceId) {
  playing[id] = false
  if (props.compact) {
    if (inView[id] && canScrollVideo.value) startVideo(id)
    return
  }
  if (hovered[id] && canHoverVideo.value) {
    startVideo(id)
  }
}

/* ------------------------------------------------------------------ */
/* CTA                                                                  */
/* ------------------------------------------------------------------ */

function isRouteAction(
  card: FairHomeV2ServiceCard,
): card is FairHomeV2ServiceCard & {
  action: { type: 'route'; to: string }
} {
  return card.action.type === 'route'
}

function handleCtaClick(card: FairHomeV2ServiceCard) {
  if (card.action.type === 'route') {
    void router.push(card.action.to)
    return
  }
  // T-005 — a guest signs up first, like every other card here (their routes are `requiresAuth`).
  if (!useTokenStore().isAuthenticated()) {
    useAuthModal().showAuthModal('signIn')
    return
  }
  styleQuiz.open()
}
</script>

<template>
  <section
    :class="rootClass"
    aria-label="services"
  >
    <header class="fair-home-v2-services__heading">
      <h2 class="fair-home-v2-services__title">
        {{ t('fair_home_v2.services.title') }}
      </h2>
    </header>

    <div
      ref="gridRef"
      class="fair-home-v2-services__grid"
    >
      <article
        v-for="card in cards"
        :key="card.id"
        class="fair-home-v2-services__card"
        :class="{ 'fair-home-v2-services__card--playing': playing[card.id] }"
        @pointerenter="handlePointerEnter(card.id)"
        @pointerleave="handlePointerLeave(card.id)"
      >
        <div
          :ref="(el) => setMediaRef(card.id, el)"
          class="fair-home-v2-services__media"
        >
          <FairImage
            class="fair-home-v2-services__image"
            :src="card.imageUrl"
            :alt="t(card.imageAltKey)"
            loading="lazy"
          />
          <video
            :ref="(el) => setVideoRef(card.id, el)"
            class="fair-home-v2-services__video"
            :src="card.videoUrl"
            :poster="card.imageUrl"
            muted
            playsinline
            preload="auto"
            disablepictureinpicture
            disableremoteplayback
            aria-hidden="true"
            tabindex="-1"
            @playing="handlePlaying(card.id)"
            @ended="handleEnded(card.id)"
          />
        </div>

        <div class="fair-home-v2-services__content">
          <div class="fair-home-v2-services__text">
            <h3 class="fair-home-v2-services__label">
              {{ t(card.titleKey) }}
            </h3>
            <p class="fair-home-v2-services__description">
              {{ t(card.descriptionKey) }}
            </p>
          </div>

          <RouterLink
            v-if="isRouteAction(card)"
            class="fair-home-v2-services__cta"
            :to="card.action.to"
          >
            {{ t(card.ctaKey) }}
          </RouterLink>
          <button
            v-else
            type="button"
            class="fair-home-v2-services__cta"
            @click="handleCtaClick(card)"
          >
            {{ t(card.ctaKey) }}
          </button>
        </div>
      </article>
    </div>
  </section>
</template>
